/*
 * Postgres 컬럼이 snake_case 라 행 → 도메인 변환 지점에서만 규칙을 끈다
 * (변환 이후 도메인 타입은 전부 camelCase — matching.api 와 같은 이유·같은 방식).
 */
/* eslint-disable @typescript-eslint/naming-convention */
import { getAuthedClient } from '~/common/utils/supabase.util.ts';

import type { AdminOrder, AdminReview, AdminTutor } from '~/features/admin/types/admin.type';

/**
 * 관리자 어댑터.
 *
 * @description 여기서 조회하는 건 전부 **남의 데이터**다. 그 접근을 앱이 판정하지 않는다 —
 *  RLS 의 `private.is_admin()` 이 통과시키지 않으면 관리자가 아닌 계정에는 빈 결과가 돌아온다.
 *  그래서 이 파일에는 "관리자인지 확인하는" 코드가 없다. 화면의 가드는 안내일 뿐이다.
 *
 *  ⚠️ 집계(클래스 수·완료 수·매출)를 SQL 한 번으로 뽑는 게 이상적이지만, PostgREST 로는 여러 테이블의
 *  집계를 한 질의로 묶기 어렵다. 지도사 수가 적은 도메인이라 목록을 각각 받아 앱에서 합친다 —
 *  지도사가 수백 명 단위가 되면 DB 뷰나 RPC 로 옮길 것.
 */

interface TutorRow {
  id: string;
  slug: string | null;
  nickname: string;
  career_label: string | null;
  rating: number | null;
  review_count: number | null;
  is_suspended: boolean;
  suspended_reason: string | null;
  created_at: string;
}

export async function fetchAdminTutorsApi(): Promise<AdminTutor[]> {
  const client = await getAuthedClient();
  if (!client) return [];

  const { data, error } = await client
    .from('profiles')
    .select(
      'id, slug, nickname, career_label, rating, review_count, is_suspended, suspended_reason, created_at',
    )
    .eq('role', 'tutor')
    .order('created_at', { ascending: true });
  if (error) throw error;

  const tutors = (data as unknown as TutorRow[] | null) ?? [];
  if (tutors.length === 0) return [];

  // 집계 3종을 한 번씩 받아 앱에서 합친다(위 주석의 이유).
  const [classesRes, requestsRes, ordersRes] = await Promise.all([
    client.from('classes').select('tutor_id'),
    client.from('class_requests').select('tutor_id, status'),
    client.from('orders').select('tutor_id, amount, status'),
  ]);
  if (classesRes.error) throw classesRes.error;
  if (requestsRes.error) throw requestsRes.error;
  if (ordersRes.error) throw ordersRes.error;

  const countBy = <T>(rows: T[], key: (row: T) => string | null): Record<string, number> =>
    rows.reduce<Record<string, number>>((acc, row) => {
      const id = key(row);
      if (id) acc[id] = (acc[id] ?? 0) + 1;
      return acc;
    }, {});

  const classCounts = countBy(
    (classesRes.data as { tutor_id: string }[] | null) ?? [],
    row => row.tutor_id,
  );
  const completedCounts = countBy(
    ((requestsRes.data as { tutor_id: string; status: string }[] | null) ?? []).filter(
      row => row.status === 'completed',
    ),
    row => row.tutor_id,
  );

  const paidAmounts = (
    (ordersRes.data as { tutor_id: string | null; amount: number; status: string }[] | null) ?? []
  )
    .filter(row => row.status === 'paid')
    .reduce<Record<string, number>>((acc, row) => {
      if (row.tutor_id) acc[row.tutor_id] = (acc[row.tutor_id] ?? 0) + row.amount;
      return acc;
    }, {});

  return tutors.map(row => ({
    id: row.id,
    slug: row.slug ?? row.id,
    name: row.nickname,
    careerLabel: row.career_label ?? '',
    rating: row.rating === null ? null : Number(row.rating),
    reviewCount: row.review_count ?? 0,
    classCount: classCounts[row.id] ?? 0,
    completedCount: completedCounts[row.id] ?? 0,
    paidAmount: paidAmounts[row.id] ?? 0,
    isSuspended: row.is_suspended,
    suspendedReason: row.suspended_reason ?? undefined,
    createdAt: row.created_at,
  }));
}

interface AdminOrderRow {
  id: string;
  class_id: string;
  class_title: string;
  amount: number;
  status: string;
  payer_name: string | null;
  paid_at: string | null;
  created_at: string;
  buyer: { nickname: string } | null;
  tutor: { nickname: string } | null;
}

export async function fetchAdminOrdersApi(): Promise<AdminOrder[]> {
  const client = await getAuthedClient();
  if (!client) return [];

  const { data, error } = await client
    .from('orders')
    .select(
      'id, class_id, class_title, amount, status, payer_name, paid_at, created_at, buyer:profiles!orders_profile_id_fkey(nickname), tutor:profiles!orders_tutor_id_fkey(nickname)',
    )
    .order('created_at', { ascending: false });
  if (error) throw error;

  return ((data as unknown as AdminOrderRow[] | null) ?? []).map(row => ({
    id: row.id,
    classId: row.class_id,
    classTitle: row.class_title,
    amount: row.amount,
    status: row.status,
    payerName: row.payer_name ?? undefined,
    buyerName: row.buyer?.nickname ?? '',
    tutorName: row.tutor?.nickname ?? '',
    paidAt: row.paid_at ?? undefined,
    createdAt: row.created_at,
  }));
}

/**
 * 입금 확인 — 주문을 결제 완료로 바꾼다.
 * @description 확인한 사람과 시각은 여기서 보내지 않는다. DB 트리거가 박는다 —
 *  앱이 채우면 위조할 수 있고, 무엇보다 "누가 확인했는가"는 앱을 우회한 경로에서도 남아야 한다.
 */
export async function confirmOrderPaidApi(orderId: string): Promise<void> {
  const client = await getAuthedClient();
  if (!client) throw new Error('로그인이 필요합니다.');

  const { error } = await client.from('orders').update({ status: 'paid' }).eq('id', orderId);
  if (error) throw error;
}

interface AdminReviewRow {
  id: string;
  class_id: string;
  rating: number;
  body: string | null;
  is_hidden: boolean;
  created_at: string;
  guardian: { nickname: string } | null;
}

export async function fetchAdminReviewsApi(tutorId: string): Promise<AdminReview[]> {
  const client = await getAuthedClient();
  if (!client) return [];

  const { data, error } = await client
    .from('reviews')
    .select(
      'id, class_id, rating, body, is_hidden, created_at, guardian:profiles!reviews_guardian_id_fkey(nickname)',
    )
    .eq('tutor_id', tutorId)
    .order('created_at', { ascending: false });
  if (error) throw error;

  return ((data as unknown as AdminReviewRow[] | null) ?? []).map(row => ({
    id: row.id,
    guardianName: row.guardian?.nickname ?? '',
    classId: row.class_id,
    rating: row.rating,
    body: row.body ?? undefined,
    isHidden: row.is_hidden,
    createdAt: row.created_at,
  }));
}

/*
  제재 두 가지는 테이블 UPDATE 가 아니라 **DB 함수**로 한다.

  0013 이 평점 조작을 막으려고 profiles·reviews 의 UPDATE 를 컬럼 단위로 좁혔고, 그 결과
  is_suspended·is_hidden 은 아무도 직접 쓸 수 없다. 컬럼 권한은 역할 단위라 "관리자에게만" 열 수
  없는데, authenticated 에 열면 **정지된 지도사가 자기 정지를 스스로 해제**할 수 있다.
  그래서 권한 검사를 품은 정의자 권한 함수로 처리한다(마이그레이션 0018).
*/

/**
 * 후기 숨김/해제.
 * @description 지우지 않고 숨긴다 — 삭제하면 조치 이력이 사라져 왜 없어졌는지 설명할 수 없고
 *  분쟁 시 되돌릴 수도 없다. 숨기면 평점 집계에서도 빠진다(트리거가 `not is_hidden` 만 센다).
 */
export async function setReviewHiddenApi(reviewId: string, isHidden: boolean): Promise<void> {
  const client = await getAuthedClient();
  if (!client) throw new Error('로그인이 필요합니다.');

  const { error } = await client.rpc('admin_set_review_hidden', {
    target_id: reviewId,
    hidden: isHidden,
  });
  if (error) throw error;
}

/** 활동 정지/해제 — 계정을 지우지 않는다(이력이 사라지면 조치를 설명할 수 없다). */
export async function setTutorSuspendedApi(
  tutorId: string,
  isSuspended: boolean,
  reason?: string,
): Promise<void> {
  const client = await getAuthedClient();
  if (!client) throw new Error('로그인이 필요합니다.');

  const { error } = await client.rpc('admin_set_tutor_suspended', {
    target_id: tutorId,
    suspended: isSuspended,
    reason: reason?.trim() || null,
  });
  if (error) throw error;
}
